// src/components/financials/BatchEntryForm.js
// Updated: Monday, July 27, 2026 at 10:36 PM EDT

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  MenuItem, Table, TableBody, TableCell, TableHead, TableRow, 
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, 
  Alert, Tabs, Tab, CircularProgress, IconButton, Autocomplete, Tooltip, createFilterOptions,
  LinearProgress 
} from '@mui/material';
import { 
  AddCircle, CheckCircle, Visibility, 
  Edit, History, Restore, Block, Add, CallSplit, Delete, Save 
} from '@mui/icons-material';
import { db } from '../../services/firebase';
import { 
  collection, addDoc, getDocs, doc, updateDoc, 
  serverTimestamp, query, where, writeBatch, orderBy, limit, deleteDoc 
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { postContributionBatch, voidPostedContribution } from '../../services/financialPostingService';

const PAYMENT_METHODS = ['Check', 'Cash', 'Online', 'Zelle', 'Cash App'];

const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.label || "",
});

const BatchEntryForm = () => {
  const { userProfile, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0); 
  const [loading, setLoading] = useState(false);
  
  // Data
  const [funds, setFunds] = useState([]);
  const [givingTypes, setGivingTypes] = useState([]); 
  const [members, setMembers] = useState([]); 
  
  // Batch State
  const [isStarted, setIsStarted] = useState(false);
  const [batchHeader, setBatchHeader] = useState({ name: '', date: new Date().toISOString().split('T')[0], totalAmount: '', status: 'draft' });
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [transactions, setTransactions] = useState([]);
  
  // Entry State
  const [entry, setEntry] = useState({ 
      donor: null, 
      inputValue: '', 
      amount: '', 
      fundId: '', 
      donationType: null, 
      method: 'Check', 
      splits: [] 
  }); 
  const [donorKey, setDonorKey] = useState(0);

  // Modal States
  const [reviewBatch, setReviewBatch] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [splitOpen, setSplitOpen] = useState(false);
  const [tempSplits, setTempSplits] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [historyBatches, setHistoryBatches] = useState([]);

  // Void Logic State
  const [voidDialog, setVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [txToVoid, setTxToVoid] = useState(null);

  const churchId = userProfile?.churchId;
  const isSuperAdmin = userProfile?.role === 'Super Administrator';

  const fetchFunds = useCallback(async () => {
    if (!churchId) return;
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
    setFunds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, [churchId]);

  const fetchGivingTypes = useCallback(async () => {
    if (!churchId) return;
    try {
      const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'donationTypes'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => a.name.localeCompare(b.name));
      setGivingTypes(list);
    } catch(e) { console.error(e); }
  }, [churchId]);

  const fetchMembers = useCallback(async () => {
    if (!churchId) return;
    try {
        const uniqueMap = new Map();

        const usersQ = query(collection(db, 'users'), where('churchId', '==', churchId));
        const usersSnap = await getDocs(usersQ);
        usersSnap.docs.forEach(d => {
            const name = `${d.data().firstName || ''} ${d.data().lastName || ''}`.trim();
            if (name) {
                uniqueMap.set(name.toLowerCase(), { id: d.id, label: name, type: 'user' });
            }
        });

        const unitsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'));
        unitsSnap.docs.forEach(d => {
            const data = d.data();
            
            // Skip this profile if it has been merged into someone else
            if (data.merged) return;

            const name = data.name || "Unknown";
            const type = data.isGuest ? 'guest' : 'unit';
            if (!uniqueMap.has(name.toLowerCase())) {
                uniqueMap.set(name.toLowerCase(), { id: d.id, label: name, type });
            }
        });

        const finalList = Array.from(uniqueMap.values()).sort((a,b) => a.label.localeCompare(b.label));
        setMembers(finalList);

    } catch (e) {
        console.error("Error fetching members:", e);
    }
  }, [churchId]);

  const fetchDrafts = useCallback(async () => {
    if (!churchId) return;
    const q = query(collection(db, 'churches', churchId, 'financials', 'data', 'batches'), where('status', '==', 'draft'));
    const snap = await getDocs(q);
    setDrafts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, [churchId]);

  const fetchHistory = useCallback(async () => {
    if (!churchId) return;
    const q = query(collection(db, 'churches', churchId, 'financials', 'data', 'batches'), where('status', 'in', ['posted', 'rejected']), orderBy('createdAt', 'desc'), limit(20));
    const snap = await getDocs(q);
    setHistoryBatches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, [churchId]);

  useEffect(() => {
    if (!churchId) return;
    fetchFunds(); 
    fetchGivingTypes(); 
    fetchMembers();
    if (activeTab === 1) fetchDrafts();
    if (activeTab === 2) fetchHistory();
  }, [churchId, activeTab, fetchFunds, fetchGivingTypes, fetchMembers, fetchDrafts, fetchHistory]);

  const markBatchAsEdited = async (batchId) => {
    if (!batchId) return;
    await updateDoc(doc(db, 'churches', churchId, 'financials', 'data', 'batches', batchId), {
      lastUpdatedBy: currentUser.uid, lastUpdatedByName: `${userProfile.firstName} ${userProfile.lastName}`, updatedAt: serverTimestamp()
    });
  };

  const handleStartLocalBatch = () => {
    if (!batchHeader.totalAmount) return alert("Enter a control total.");
    setIsStarted(true);
  };

  const handleEditBatch = async (batch) => {
    setReviewBatch(null);
    setCurrentBatchId(batch.id);
    setIsStarted(true);
    setBatchHeader({ name: batch.name, date: batch.date, totalAmount: batch.totalAmount, status: batch.status });
    const q = query(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'), where('batchId', '==', batch.id));
    const snap = await getDocs(q);
    setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setActiveTab(0);
  };

  const handleUpdateHeader = async () => {
    if (!currentBatchId) return;
    await updateDoc(doc(db, 'churches', churchId, 'financials', 'data', 'batches', currentBatchId), {
      ...batchHeader, totalAmount: parseFloat(batchHeader.totalAmount), lastUpdatedBy: currentUser.uid 
    });
    alert("Batch Header Updated");
  };

  const handleTypeChange = (newValue) => {
      setEntry(prev => ({ 
          ...prev, 
          donationType: newValue, 
          fundId: newValue?.defaultFundId || '' 
      }));
  };

  const handleOpenSplit = () => {
      if (!entry.amount || parseFloat(entry.amount) <= 0) return alert("Enter Amount first.");
      setTempSplits(entry.splits.length > 0 ? [...entry.splits] : [{ typeId: '', amount: '' }, { typeId: '', amount: '' }]);
      setSplitOpen(true);
  };

  const handleSaveSplit = () => {
      const totalSplit = tempSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
      if (Math.abs(totalSplit - parseFloat(entry.amount)) > 0.01) return alert("Split total doesn't match entry amount.");
      setEntry({ ...entry, splits: tempSplits.filter(s => s.typeId && s.amount), donationType: { name: 'Multiple Types' } }); 
      setSplitOpen(false);
  };

  const createGuestProfile = async (name) => {
      const ref = await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'), {
          name: name, isGuest: true, type: 'guest', createdAt: serverTimestamp()
      });
      const newMember = { id: ref.id, label: `${name} (Guest)`, type: 'guest' };
      setMembers(prev => [...prev, newMember].sort((a,b) => a.label.localeCompare(b.label)));
      return { id: ref.id, label: name };
  };

  const handleAddTransaction = async () => {
    let donorId = entry.donor?.id;
    let donorName = entry.donor?.label;
    let donorType = entry.donor?.type; 

    if (!donorId && entry.inputValue) {
        const normalizedInput = entry.inputValue.trim().toLowerCase();
        const existingMember = members.find(m => m.label.toLowerCase() === normalizedInput);

        if (existingMember) {
            donorId = existingMember.id;
            donorName = existingMember.label;
            donorType = existingMember.type; 
        } else {
            donorName = entry.inputValue;
            donorId = 'NEW';
            donorType = 'guest'; 
        }
    }

    if (!donorName || !entry.amount) return alert("Donor and Amount are required."); 

    if (donorId === 'NEW' && currentBatchId) {
       const newGuest = await createGuestProfile(donorName);
       donorId = newGuest.id;
       donorName = newGuest.label;
    }

    let itemsToAdd = [];
    const groupKey = Date.now().toString();

    const commonData = {
        donorId, 
        donorName,
        donorType, 
        date: batchHeader.date,
        method: entry.method,
        status: 'draft',
    };

    if (entry.splits.length > 0) {
        itemsToAdd = entry.splits.map(s => {
            const typeObj = givingTypes.find(t => t.id === s.typeId);
            return {
                ...commonData,
                amount: parseFloat(s.amount),
                donationType: typeObj?.name || 'Unknown',
                fundId: typeObj?.defaultFundId,
                fundName: funds.find(f => f.id === typeObj?.defaultFundId)?.name || 'Unknown',
                splitGroup: groupKey
            };
        });
    } else {
        if (!entry.fundId) return alert("Select a Type with a default fund or select a fund manually.");
        itemsToAdd.push({
            ...commonData,
            amount: parseFloat(entry.amount),
            donationType: entry.donationType.name,
            fundId: entry.fundId,
            fundName: funds.find(f => f.id === entry.fundId)?.name,
            splitGroup: null
        });
    }

    if (currentBatchId) {
        for (const item of itemsToAdd) {
            const ref = await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'), { ...item, batchId: currentBatchId });
            setTransactions(prev => [...prev, { id: ref.id, ...item }]);
        }
        await markBatchAsEdited(currentBatchId);
    } else {
        const localItems = itemsToAdd.map(i => ({ ...i, id: Math.random().toString(36).substr(2, 9) }));
        setTransactions(prev => [...prev, ...localItems]);
    }

    setEntry({ ...entry, donor: null, inputValue: '', amount: '', splits: [], fundId: '', donationType: null }); 
    setDonorKey(prev => prev + 1);
  };

  const handlePreDelete = (tx) => {
    if (tx.status === 'posted') {
      setTxToVoid(tx);
      setVoidReason('');
      setVoidDialog(true);
    } else {
      handleDeleteTransaction(tx.id);
    }
  };

  const handleDeleteTransaction = async (txId) => {
    if(!window.confirm("Remove this draft entry?")) return;
    if (currentBatchId) {
        await deleteDoc(doc(db, 'churches', churchId, 'financials', 'data', 'transactions', txId));
        await markBatchAsEdited(currentBatchId);
    } 
    setTransactions(transactions.filter(t => t.id !== txId));
  };

  const confirmVoid = async () => {
    if (!voidReason.trim()) return alert("Reason required for audit trail.");
    setLoading(true);
    try {
      await voidPostedContribution(churchId, txToVoid.id, voidReason.trim());
      alert("Transaction voided and a reversing entry was created.");
      setVoidDialog(false);
      setTxToVoid(null);
      const q = query(
        collection(db, 'churches', churchId, 'financials', 'data', 'transactions'),
        where('batchId', '==', currentBatchId)
      );
      const snap = await getDocs(q);
      setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      await fetchFunds();
    } catch (e) {
      alert("Void failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveBatchToFirestore = async (submitToVerification = false) => {
      if (transactions.length === 0) return alert("No transactions to save.");
      setLoading(true);
      try {
          const batch = writeBatch(db);
          const batchesRef = collection(db, 'churches', churchId, 'financials', 'data', 'batches');
          const batchDoc = doc(batchesRef); 
          
          batch.set(batchDoc, {
              ...batchHeader,
              totalAmount: parseFloat(batchHeader.totalAmount),
              status: 'draft',
              createdBy: currentUser.uid,
              createdByName: `${userProfile.firstName} ${userProfile.lastName}`,
              createdAt: serverTimestamp()
          });

          const txCollectionRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');

          for (const tx of transactions) {
              const txRef = doc(txCollectionRef); 
              let finalDonorId = tx.donorId;

              if (tx.donorId === 'NEW') {
                  const guestRef = await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'), {
                      name: tx.donorName, isGuest: true, type: 'guest', createdAt: serverTimestamp()
                  });
                  finalDonorId = guestRef.id;
              }

              const { id, splits, ...cleanTxData } = tx; 
              batch.set(txRef, { ...cleanTxData, donorId: finalDonorId, batchId: batchDoc.id, date: batchHeader.date, status: 'draft' });
          }

          await batch.commit();
          setIsStarted(false); setTransactions([]); setBatchHeader({ name: '', date: new Date().toISOString().split('T')[0], totalAmount: '', status: 'draft' });
          
          fetchMembers();
          if (submitToVerification) setActiveTab(1);
          else alert("Batch saved successfully.");
      } catch (e) { alert("Save failed: " + e.message); }
      setLoading(false);
  };

  const openReviewModal = async (batch) => {
    setReviewBatch(batch);
    setReviewLoading(true);
    const q = query(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'), where('batchId', '==', batch.id));
    const snap = await getDocs(q);
    setReviewItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setReviewLoading(false);
  };

  const handlePostBatch = async () => {
    if (!window.confirm("Post to Ledger? This will update account balances.")) return;
    setLoading(true);
    try {
      await postContributionBatch(churchId, reviewBatch.id);
      setReviewBatch(null);
      await Promise.all([fetchDrafts(), fetchHistory(), fetchFunds()]);
      alert("Batch posted successfully.");
    } catch (e) {
      alert("Posting error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectBatch = async () => {
    if (!rejectReason.trim()) return alert("Reason required.");
    try {
      await updateDoc(doc(db, 'churches', churchId, 'financials', 'data', 'batches', reviewBatch.id), {
        status: 'rejected', rejectionReason: rejectReason, rejectedBy: currentUser.uid, rejectedByName: `${userProfile.firstName} ${userProfile.lastName}`, rejectedAt: serverTimestamp()
      });
      setRejectDialog(false); setReviewBatch(null); setRejectReason(''); fetchDrafts();
    } catch (e) { console.error(e); }
  };

  const handleReopenBatch = async (batchId) => {
    if (!window.confirm("Re-open?")) return;
    await updateDoc(doc(db, 'churches', churchId, 'financials', 'data', 'batches', batchId), { status: 'draft', rejectionReason: null });
    fetchHistory();
  };

  const handleDeleteDraftBatch = async (batch) => {
    if (batch.status !== 'draft') {
        alert("Only draft batches can be deleted.");
        return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete the batch "${batch.name}"? All associated draft transactions will be destroyed.`)) {
        return;
    }

    setLoading(true);
    try {
        const batchWrite = writeBatch(db);

        const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
        const q = query(txRef, where('batchId', '==', batch.id));
        const txSnap = await getDocs(q);

        txSnap.docs.forEach(tDoc => {
            batchWrite.delete(tDoc.ref);
        });

        const batchRef = doc(db, 'churches', churchId, 'financials', 'data', 'batches', batch.id);
        batchWrite.delete(batchRef);

        await batchWrite.commit();
        
        alert("Batch and all associated transactions successfully deleted.");
        fetchDrafts(); 
        
    } catch (error) {
        console.error(error);
        alert("Error deleting batch: " + error.message);
    }
    setLoading(false);
  };

  const enteredTotal = reviewItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const controlTotal = Number(reviewBatch?.totalAmount) || 0;
  const isBalanced = Math.abs(controlTotal - enteredTotal) < 0.01;
  const hasConflict = (reviewBatch?.createdBy === currentUser.uid || reviewBatch?.lastUpdatedBy === currentUser.uid);
  const showFundSelector = entry.donationType && !entry.donationType.defaultFundId && entry.splits.length === 0;

  return (
    <Box>
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="Data Entry" icon={<AddCircle />} iconPosition="start" />
        <Tab label={`Verification Queue (${drafts.length})`} icon={<CheckCircle />} iconPosition="start" />
        <Tab label="History & Audit" icon={<History />} iconPosition="start" />
      </Tabs>

      {/* TAB 0: ENTRY */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          {!isStarted ? (
            <Box>
              <Typography variant="h6" gutterBottom>Start New Batch</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}><TextField fullWidth label="Batch Name" value={batchHeader.name} onChange={(e)=>setBatchHeader({...batchHeader, name: e.target.value})} /></Grid>
                <Grid item xs={6} md={4}><TextField fullWidth type="date" label="Date" InputLabelProps={{ shrink: true }} value={batchHeader.date} onChange={(e)=>setBatchHeader({...batchHeader, date: e.target.value})} /></Grid>
                <Grid item xs={6} md={4}><TextField fullWidth type="number" label="Control Total ($)" value={batchHeader.totalAmount} onChange={(e)=>setBatchHeader({...batchHeader, totalAmount: e.target.value})} /></Grid>
                <Grid item xs={12}><Button variant="contained" onClick={handleStartLocalBatch}>Initialize Entry Row</Button></Grid>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1, alignItems: 'center' }}>
                <Box>
                    <TextField size="small" label="Batch Name" value={batchHeader.name} onChange={(e)=>setBatchHeader({...batchHeader, name: e.target.value})} sx={{ mr: 2 }} />
                    <TextField size="small" type="number" label="Control Total" value={batchHeader.totalAmount} onChange={(e)=>setBatchHeader({...batchHeader, totalAmount: e.target.value})} sx={{ width: 120 }} />
                </Box>
                {currentBatchId && <Button size="small" variant="outlined" onClick={handleUpdateHeader}>Update Header</Button>}
              </Box>

              <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2, alignItems: 'center', p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                <Box sx={{ flex: 4, minWidth: 200, mb: { xs: 2, md: 0 } }}>
                  <Autocomplete 
                    key={donorKey} 
                    freeSolo 
                    autoSelect 
                    autoHighlight 
                    options={members} 
                    filterOptions={filterOptions} 
                    getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')} 
                    renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                            <li key={option.id ? `${key}-${option.id}` : key} {...otherProps}>
                                {option.label}
                                {option.type === 'guest' && <Chip label="Guest" size="small" sx={{ ml: 1, height: 20 }} />}
                            </li>
                        );
                    }}
                    value={entry.donor} 
                    onInputChange={(e, newInputValue) => {
                        setEntry(prev => ({ ...prev, inputValue: newInputValue }));
                    }}
                    onChange={(e, v) => {
                        if (typeof v === 'string') setEntry(prev => ({ ...prev, donor: { label: v, id: null }, inputValue: v })); 
                        else if (v && v.inputValue) setEntry(prev => ({ ...prev, donor: { label: v.inputValue, id: null }, inputValue: v.inputValue }));
                        else setEntry(prev => ({ ...prev, donor: v, inputValue: v ? v.label : '' }));
                    }} 
                    renderInput={(p) => <TextField {...p} label="Donor (Type to add guest)" autoFocus={donorKey > 0} />} 
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', md: 110 }, mb: { xs: 2, md: 0 } }}>
                  <TextField fullWidth label="Amount" type="number" value={entry.amount} onChange={(e)=>setEntry({...entry, amount: e.target.value})} />
                </Box>
                <Box sx={{ flex: 3, display: 'flex', alignItems: 'center', gap: 1, minWidth: 150, mb: { xs: 2, md: 0 } }}>
                   <Autocomplete 
                        fullWidth 
                        autoSelect 
                        autoHighlight 
                        options={givingTypes} 
                        getOptionLabel={(o)=>o.name||''} 
                        value={entry.donationType} 
                        onChange={(e,v)=>handleTypeChange(v)} 
                        renderInput={(p)=><TextField {...p} label="Giving Type" />} 
                        disabled={entry.splits.length > 0} 
                    />
                   <Tooltip title="Split Types"><IconButton color="secondary" onClick={handleOpenSplit} size="small"><CallSplit /></IconButton></Tooltip>
                </Box>
                {showFundSelector && (
                   <Box sx={{ flex: 3, minWidth: 150, mb: { xs: 2, md: 0 } }}><Autocomplete fullWidth options={funds} getOptionLabel={(o)=>o.name||''} value={funds.find(f=>f.id===entry.fundId)||null} onChange={(e,v)=>setEntry({...entry, fundId:v?.id||''})} renderInput={(p)=><TextField {...p} label="Manual Fund" />} /></Box>
                )}
                <Box sx={{ width: { xs: '100%', md: 120 }, mb: { xs: 2, md: 0 } }}>
                  <TextField select fullWidth label="Method" value={entry.method} onChange={(e)=>setEntry({...entry, method: e.target.value})}>{PAYMENT_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}</TextField>
                </Box>
                <IconButton color="primary" onClick={handleAddTransaction} sx={{ bgcolor: 'primary.light', color: 'white' }}><Add /></IconButton>
              </Box>

              <Table size="small" sx={{ mt: 3 }}>
                <TableHead><TableRow><TableCell>Donor</TableCell><TableCell>Amount</TableCell><TableCell>Type</TableCell><TableCell>Fund</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                <TableBody>
                  {transactions.map(t => (
                    <TableRow key={t.id} sx={{ bgcolor: t.status === 'voided' ? '#ffebee' : 'inherit', textDecoration: t.status === 'voided' ? 'line-through' : 'none' }}>
                        <TableCell>
                            {(t.donorType === 'guest' || (t.donorId === 'NEW' && t.donorType !== 'user' && t.donorType !== 'unit')) && <Chip label="Guest" size="small" color="info" sx={{mr:1}} />}
                            {t.donorName} 
                            {t.status === 'voided' && <Chip label="VOIDED" size="small" color="error" />}
                        </TableCell>
                        <TableCell>${t.amount.toFixed(2)}</TableCell>
                        <TableCell>{t.donationType}</TableCell>
                        <TableCell>{t.splits && t.splits.length > 1 ? "Multiple (Legacy)" : t.fundName}</TableCell>
                        <TableCell align="right">
                           <IconButton color="error" onClick={()=>handlePreDelete(t)}>
                               {t.status === 'posted' ? <Block /> : <Delete />}
                           </IconButton>
                        </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length > 0 && (
                      <TableRow sx={{ bgcolor: '#eee' }}><TableCell colSpan={1} sx={{ fontWeight: 'bold' }}>Total</TableCell><TableCell align="left" sx={{ fontWeight: 'bold' }}>${transactions.reduce((acc,t)=>acc+t.amount, 0).toFixed(2)}</TableCell><TableCell colSpan={3}/></TableRow>
                  )}
                </TableBody>
              </Table>
              
              <Box sx={{ mt: 3, textAlign: 'right' }}>
                {currentBatchId ? (
                    <Button onClick={() => { setCurrentBatchId(null); setIsStarted(false); setTransactions([]); }} sx={{ mr: 1 }}>Close</Button>
                ) : (
                    <>
                        <Button variant="outlined" onClick={() => saveBatchToFirestore(false)} sx={{ mr: 1 }} startIcon={<Save />} disabled={loading}>Finish Later</Button>
                        <Button variant="contained" color="success" onClick={() => saveBatchToFirestore(true)} startIcon={<CheckCircle />} disabled={loading}>Submit to Verification</Button>
                    </>
                )}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* TABS 1 & 2 */}
      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          <Table>
            <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Batch Name</TableCell><TableCell>Created By</TableCell><TableCell align="right">Total</TableCell><TableCell align="center">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {drafts.map(b => (
                <TableRow key={b.id}>
                  <TableCell>{b.date}</TableCell><TableCell>{b.name}</TableCell><TableCell>{b.createdByName}</TableCell><TableCell align="right">${Number(b.totalAmount).toFixed(2)}</TableCell>
                  <TableCell align="center">
                      <IconButton onClick={() => handleEditBatch(b)} color="primary"><Edit /></IconButton>
                      <IconButton onClick={() => openReviewModal(b)} color="success"><Visibility /></IconButton>
                      <Tooltip title="Delete Draft Batch & Transactions">
                          <IconButton onClick={() => handleDeleteDraftBatch(b)} color="error"><Delete /></IconButton>
                      </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {drafts.length === 0 && <TableRow><TableCell colSpan={5} align="center">No pending batches.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      )}
      {activeTab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Table>
            <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Batch</TableCell><TableCell>Status</TableCell><TableCell>Reason</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {historyBatches.map(b => (
                <TableRow key={b.id} hover>
                  <TableCell>{b.date}</TableCell><TableCell>{b.name}</TableCell><TableCell><Chip label={b.status} color={b.status === 'posted' ? 'success' : 'error'} size="small" /></TableCell><TableCell>{b.rejectionReason}</TableCell>
                  <TableCell>{b.status === 'rejected' && <IconButton onClick={() => handleReopenBatch(b.id)}><Restore /></IconButton>}<IconButton onClick={() => openReviewModal(b)}><Visibility /></IconButton></TableCell>
                </TableRow>
              ))}
              {historyBatches.length === 0 && <TableRow><TableCell colSpan={5} align="center">No history available.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* REVIEW & REJECT MODALS */}
      <Dialog open={!!reviewBatch && !rejectDialog} onClose={() => setReviewBatch(null)} maxWidth="md" fullWidth>
        <DialogTitle>Review: {reviewBatch?.name}</DialogTitle>
        <DialogContent dividers>
          {reviewLoading ? <CircularProgress /> : (
            <Box>
              {hasConflict && !isSuperAdmin && reviewBatch?.status === 'draft' && <Alert severity="warning" sx={{ mb: 2 }}>Conflict: You created/edited this. Another admin must post.</Alert>}
              <Alert severity={isBalanced ? "success" : "error"} sx={{ mb: 2 }}>{isBalanced ? "Balanced" : `Mismatch: Diff is $${(controlTotal - enteredTotal).toFixed(2)}`}</Alert>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}><TableRow><TableCell>Donor</TableCell><TableCell>Amount</TableCell><TableCell>Type</TableCell><TableCell>Fund</TableCell></TableRow></TableHead>
                <TableBody>{reviewItems.map(item => (<TableRow key={item.id}><TableCell>{item.donorName}</TableCell><TableCell>${item.amount.toFixed(2)}</TableCell><TableCell>{item.splits?.length > 1 ? "Legacy Split" : item.donationType}</TableCell><TableCell>{item.splits?.length > 1 ? "Multiple" : item.fundName}</TableCell></TableRow>))}</TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewBatch(null)}>Close</Button>
          {reviewBatch?.status === 'draft' && (
            <>
              <Button color="error" onClick={() => setRejectDialog(true)}>Reject</Button>
              <Button variant="contained" color="primary" disabled={!isBalanced || (hasConflict && !isSuperAdmin)} onClick={handlePostBatch}>Post Batch</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      <Dialog open={voidDialog} onClose={() => setVoidDialog(false)}>
        <DialogTitle>Void Transaction?</DialogTitle>
        <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>This item is POSTED. It cannot be deleted. It will be voided and a reversing entry will be created.</Alert>
            <TextField autoFocus fullWidth multiline rows={2} label="Reason for Void" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g. Check bounced, Data entry error" />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setVoidDialog(false)}>Cancel</Button>
            <Button onClick={confirmVoid} variant="contained" color="error">Confirm Void</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}><DialogTitle>Reject Reason</DialogTitle><DialogContent><TextField autoFocus fullWidth multiline rows={3} value={rejectReason} onChange={(e)=>setRejectReason(e.target.value)} /></DialogContent><DialogActions><Button onClick={()=>setRejectDialog(false)}>Cancel</Button><Button onClick={handleRejectBatch} color="error">Confirm Reject</Button></DialogActions></Dialog>

      <Dialog open={splitOpen} onClose={() => setSplitOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Split Donation Types (${entry.amount})</DialogTitle>
        <DialogContent dividers>
            {tempSplits.map((split, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                    
                    {/* CHANGED: Replaced TextField select with Autocomplete */}
                    <Autocomplete
                        fullWidth
                        autoSelect
                        autoHighlight
                        options={givingTypes}
                        getOptionLabel={(o) => o.name || ''}
                        value={givingTypes.find(t => t.id === split.typeId) || null}
                        onChange={(e, v) => {
                            const newSplits = [...tempSplits]; 
                            newSplits[idx].typeId = v ? v.id : ''; 
                            setTempSplits(newSplits);
                        }}
                        renderInput={(p) => <TextField {...p} label="Giving Type" autoFocus={idx === tempSplits.length - 1} />}
                    />

                    <TextField label="Amount" type="number" value={split.amount} onChange={(e) => {
                        const newSplits = [...tempSplits]; newSplits[idx].amount = e.target.value; setTempSplits(newSplits);
                    }} />
                    <IconButton color="error" onClick={() => setTempSplits(tempSplits.filter((_, i) => i !== idx))}><Delete /></IconButton>
                </Box>
            ))}
            <Button startIcon={<AddCircle />} onClick={() => setTempSplits([...tempSplits, { typeId: '', amount: '' }])}>Add Type</Button>
        </DialogContent>
        <DialogActions><Button onClick={() => setSplitOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSaveSplit}>Confirm</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchEntryForm;